import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
    RefreshCcw,
    TrendingUp,
    Users,
    ArrowUpRight,
    ArrowDownRight,
    Target,
    BarChart3,
    PieChart as PieChartIcon,
    LineChart as LineChartIcon,
    Activity,
    DollarSign,
    Percent,
    AlertCircle,
} from 'lucide-react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import platformValueApi, { PlatformValueAllResponse } from '../../lib/api/platform-value';
import { cn } from '@/lib/utils';

const COLORS = {
    primary: '#2563eb',
    secondary: '#10b981',
    accent: '#8b5cf6',
    warning: '#f59e0b',
    danger: '#ef4444',
    grid: '#f3f4f6',
    text: '#737373',
};

function fmtCurrency(value: number | null, digits = 0): string {
    if (value === null || value === undefined || !Number.isFinite(value)) return '—';
    return `$${value.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
}

function fmtPercent(value: number | null): string {
    if (value === null || value === undefined || !Number.isFinite(value)) return '—';
    return `${value}%`;
}

function fmtNumber(value: number | null): string {
    if (value === null || value === undefined || !Number.isFinite(value)) return '—';
    return value.toLocaleString();
}

interface StatCardProps {
    title: string;
    value: string;
    change?: number | null;
    description?: string;
    icon: React.ReactNode;
    loading?: boolean;
    insufficient?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, change, description, icon, loading, insufficient }) => (
    <Card className="hover:shadow-md transition-all border-neutral-100 shadow-sm overflow-hidden group">
        <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
                <div className="p-2 bg-neutral-50 rounded-lg text-neutral-500 group-hover:bg-neutral-900 group-hover:text-white transition-colors duration-300">
                    {icon}
                </div>
                {change !== undefined && change !== null && Number.isFinite(change) && (
                    <div className={cn(
                        'flex items-center text-xs font-bold px-2 py-0.5 rounded-full',
                        change >= 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'
                    )}>
                        {change >= 0 ? <ArrowUpRight className="w-3 h-3 mr-1" /> : <ArrowDownRight className="w-3 h-3 mr-1" />}
                        {Math.abs(change)}%
                    </div>
                )}
            </div>
            {loading ? (
                <div className="space-y-2">
                    <div className="h-4 w-20 bg-neutral-100 animate-pulse rounded" />
                    <div className="h-8 w-24 bg-neutral-100 animate-pulse rounded" />
                </div>
            ) : (
                <>
                    <p className="text-sm font-medium text-neutral-500 mb-1">{title}</p>
                    <h3 className="text-2xl font-black text-neutral-900">{value}</h3>
                    {insufficient ? (
                        <p className="text-[10px] text-amber-500 font-medium mt-1 uppercase tracking-tight flex items-center gap-1">
                            <AlertCircle size={10} />
                            Awaiting more history
                        </p>
                    ) : description ? (
                        <p className="text-[10px] text-neutral-400 font-medium mt-1 uppercase tracking-tight">{description}</p>
                    ) : null}
                </>
            )}
        </CardContent>
    </Card>
);

export const PlatformValuePage: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [data, setData] = useState<PlatformValueAllResponse | null>(null);

    const fetchData = async () => {
        try {
            const allData = await platformValueApi.getAll();
            setData(allData);
        } catch (error) {
            console.error('Failed to fetch platform value data:', error);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchData();
        setRefreshing(false);
    };

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            await fetchData();
            setLoading(false);
        };
        init();
    }, []);

    const insufficient = data?.executive.insufficientData;

    return (
        <div className="flex flex-col gap-6 p-6 max-w-[1400px] mx-auto animate-in fade-in duration-500">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="space-y-1">
                    <div className="flex items-center gap-2">
                        <BarChart3 className="w-5 h-5 text-emerald-600" />
                        <h1 className="text-3xl font-black text-neutral-900 tracking-tight">Platform Value</h1>
                    </div>
                    <p className="text-sm text-neutral-400 font-medium">
                        Evaluate growth, retention, and unit economics.
                        {data?.executive.asOf && (
                            <span className="ml-2 text-neutral-300">As of {data.executive.asOf}</span>
                        )}
                    </p>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    className="h-9 font-bold bg-white text-neutral-600 hover:text-neutral-900 border-neutral-100 shadow-sm"
                    onClick={handleRefresh}
                    disabled={refreshing}
                >
                    <RefreshCcw className={cn('w-4 h-4 mr-2', refreshing && 'animate-spin')} />
                    Refresh
                </Button>
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center min-h-[400px]">
                    <span className="relative flex h-10 w-10 mb-4">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-20"></span>
                        <span className="relative inline-flex rounded-full h-10 w-10 bg-emerald-600 items-center justify-center">
                            <Activity className="text-white w-5 h-5" />
                        </span>
                    </span>
                    <span className="text-xs font-black text-neutral-400 uppercase tracking-widest">Aggregating Platform Data...</span>
                </div>
            ) : data ? (
                <div className="flex flex-col gap-8">
                    {insufficient && (
                        <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 flex items-start gap-3">
                            <AlertCircle className="text-amber-500 mt-0.5" size={16} />
                            <div className="text-xs text-amber-700">
                                <p className="font-bold mb-1">Awaiting subscription history</p>
                                <p>
                                    The daily snapshot job has not yet captured enough data to compute MRR, growth, churn, and NRR.
                                    Metrics will populate once one full day of snapshots has run.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Executive Dashboard */}
                    <div className="space-y-4">
                        <h2 className="text-xs font-black uppercase tracking-widest text-neutral-400 flex items-center gap-2">
                            <TrendingUp size={14} />
                            Executive Dashboard
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                            <StatCard
                                title="MRR"
                                value={fmtCurrency(data.executive.mrr)}
                                description="Monthly Recurring"
                                icon={<DollarSign size={18} />}
                                insufficient={data.executive.mrr === null}
                            />
                            <StatCard
                                title="ARR"
                                value={fmtCurrency(data.executive.arr)}
                                description="Annual Run Rate"
                                icon={<BarChart3 size={18} />}
                                insufficient={data.executive.arr === null}
                            />
                            <StatCard
                                title="Growth Rate"
                                value={fmtPercent(data.executive.growthRate)}
                                description="30-day"
                                icon={<ArrowUpRight size={18} />}
                                insufficient={data.executive.growthRate === null}
                            />
                            <StatCard
                                title="NRR"
                                value={fmtPercent(data.executive.nrr)}
                                description="Net Revenue Retention"
                                icon={<RefreshCcw size={18} />}
                                insufficient={data.executive.nrr === null}
                            />
                            <StatCard
                                title="Churn Rate"
                                value={fmtPercent(data.executive.churnRate)}
                                description="30-day"
                                icon={<Users size={18} />}
                                insufficient={data.executive.churnRate === null}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Revenue Breakdown */}
                        <Card className="border-neutral-100 shadow-sm overflow-hidden min-h-[400px]">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base font-bold flex items-center gap-2">
                                    <PieChartIcon className="w-4 h-4 text-neutral-400" />
                                    Revenue Breakdown
                                </CardTitle>
                                <CardDescription className="text-xs">New + Expansion vs Churn + Contraction MRR</CardDescription>
                            </CardHeader>
                            <CardContent className="h-[350px]">
                                {data.revenue.length === 0 ? (
                                    <div className="h-full flex flex-col items-center justify-center text-xs text-neutral-400 italic gap-2">
                                        <AlertCircle size={20} className="text-amber-500" />
                                        <span>Awaiting snapshot history to compute the breakdown.</span>
                                    </div>
                                ) : (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={data.revenue}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={COLORS.grid} />
                                            <XAxis dataKey="month" fontSize={10} axisLine={false} tickLine={false} tick={{ fill: COLORS.text, fontWeight: 500 }} />
                                            <YAxis fontSize={10} axisLine={false} tickLine={false} tick={{ fill: COLORS.text, fontWeight: 500 }} />
                                            <Tooltip contentStyle={{ backgroundColor: '#ffffff', border: '1px solid #f3f4f6', borderRadius: '12px', fontSize: '11px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                                            <Legend wrapperStyle={{ fontSize: '10px', fontWeight: 'bold', paddingTop: '10px' }} />
                                            <Bar dataKey="new" name="New MRR" stackId="a" fill={COLORS.secondary} radius={[0, 0, 0, 0]} />
                                            <Bar dataKey="expansion" name="Expansion" stackId="a" fill={COLORS.accent} radius={[4, 4, 0, 0]} />
                                            <Bar dataKey="churn" name="Churn" fill={COLORS.danger} radius={[4, 4, 0, 0]} />
                                            <Bar dataKey="contraction" name="Contraction" fill={COLORS.warning} radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </CardContent>
                        </Card>

                        {/* Conversion Funnel */}
                        <Card className="border-neutral-100 shadow-sm overflow-hidden min-h-[400px]">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base font-bold flex items-center gap-2">
                                    <Target className="w-4 h-4 text-neutral-400" />
                                    Conversion Funnel
                                </CardTitle>
                                <CardDescription className="text-xs">Visitor to Subscriber journey (last 30 days)</CardDescription>
                            </CardHeader>
                            <CardContent className="flex flex-col justify-center gap-6 h-[350px]">
                                {data.economics.funnel.map((step, i) => (
                                    <div key={i} className="space-y-2">
                                        <div className="flex justify-between items-center">
                                            <span className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">{step.label}</span>
                                            <div className="flex items-center gap-3">
                                                <span className="text-neutral-900 font-bold tabular-nums">{fmtNumber(step.value)}</span>
                                                <span className="text-[10px] font-black text-neutral-300 bg-neutral-50 px-2 py-0.5 rounded border border-neutral-100">
                                                    {step.percent === null ? '—' : `${step.percent}%`}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="w-full h-3 bg-neutral-50 rounded-full overflow-hidden">
                                            <div
                                                className="h-full rounded-full transition-all duration-1000 shadow-sm"
                                                style={{
                                                    width: `${step.percent ?? 0}%`,
                                                    backgroundColor: i === 0 ? COLORS.primary : i === 1 ? COLORS.accent : i === 2 ? '#4f46e5' : COLORS.secondary,
                                                }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Unit Economics */}
                        <Card className="border-neutral-100 shadow-sm overflow-hidden lg:col-span-1">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base font-bold flex items-center gap-2">
                                    <Percent className="w-4 h-4 text-neutral-400" />
                                    Unit Economics
                                </CardTitle>
                                <CardDescription className="text-xs">CAC & LTV Analysis</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6 pt-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="p-4 bg-neutral-50 rounded-xl border border-neutral-100">
                                        <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest mb-1">CAC</p>
                                        <p className="text-xl font-black text-neutral-900">
                                            {data.economics.economics.cac === null
                                                ? '—'
                                                : `$${data.economics.economics.cac.toFixed(2)}`}
                                        </p>
                                    </div>
                                    <div className="p-4 bg-neutral-50 rounded-xl border border-neutral-100">
                                        <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest mb-1">LTV</p>
                                        <p className="text-xl font-black text-neutral-900">
                                            {data.economics.economics.ltv === null
                                                ? '—'
                                                : `$${data.economics.economics.ltv.toFixed(0)}`}
                                        </p>
                                    </div>
                                </div>
                                <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                                    <div className="flex justify-between items-center mb-1">
                                        <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest">LTV / CAC Ratio</p>
                                        {data.economics.economics.ltvCacRatio !== null && data.economics.economics.ltvCacRatio >= 3 && (
                                            <span className="text-[10px] font-black bg-emerald-600 text-white px-2 py-0.5 rounded">HEALTHY</span>
                                        )}
                                    </div>
                                    <p className="text-3xl font-black text-emerald-700">
                                        {data.economics.economics.ltvCacRatio === null
                                            ? '—'
                                            : `${data.economics.economics.ltvCacRatio}x`}
                                    </p>
                                    <p className="text-[10px] text-emerald-600 font-medium mt-1 italic">Target: {'>'} 3.0x</p>
                                </div>
                                <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                                    <p className="text-[10px] font-bold text-blue-600 uppercase tracking-widest mb-1">ARPU</p>
                                    <p className="text-xl font-black text-blue-900">
                                        {data.economics.economics.arpu === null
                                            ? '—'
                                            : `$${data.economics.economics.arpu.toFixed(2)}`}
                                    </p>
                                    <p className="text-[10px] text-blue-500 font-medium mt-1 italic">Average Revenue Per User</p>
                                </div>
                                {data.economics.economics.inputs.marketingSpendUsdPerMonth === null && (
                                    <div className="text-[10px] text-amber-600 italic flex items-start gap-1">
                                        <AlertCircle size={10} className="mt-0.5" />
                                        <span>
                                            Set <code className="font-mono">PLATFORM_MARKETING_SPEND_USD_PER_MONTH</code> to enable CAC and LTV / CAC.
                                        </span>
                                    </div>
                                )}
                                {data.economics.economics.inputs.monthlyChurnSource === 'fallback' && (
                                    <div className="text-[10px] text-neutral-500 italic">
                                        LTV uses <code className="font-mono">PLATFORM_DEFAULT_CHURN_FALLBACK</code> (no observed churn yet).
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Retention */}
                        <Card className="border-neutral-100 shadow-sm overflow-hidden lg:col-span-2">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-base font-bold flex items-center gap-2">
                                    <LineChartIcon className="w-4 h-4 text-neutral-400" />
                                    Retention Analysis
                                </CardTitle>
                                <CardDescription className="text-xs">Cohort retention curves (% of users retained)</CardDescription>
                            </CardHeader>
                            <CardContent className="pt-4 overflow-x-auto">
                                <table className="w-full text-left text-[11px]">
                                    <thead>
                                        <tr className="border-b border-neutral-100">
                                            <th className="pb-3 font-black text-neutral-400 uppercase tracking-widest">Cohort</th>
                                            <th className="pb-3 font-black text-neutral-400 uppercase tracking-widest">Size</th>
                                            {[1, 2, 3, 6, 12].map(m => (
                                                <th key={m} className="pb-3 font-black text-neutral-400 uppercase tracking-widest text-center">Month {m}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-neutral-50">
                                        {data.retention.map((cohort, i) => (
                                            <tr key={i} className="group hover:bg-neutral-50/50 transition-colors">
                                                <td className="py-4 font-black text-neutral-900">{cohort.month}</td>
                                                <td className="py-4 font-bold text-neutral-500">{cohort.size}</td>
                                                {[1, 2, 3, 6, 12].map(m => {
                                                    const retainedCount = cohort.retained[m];
                                                    if (retainedCount === undefined) {
                                                        return (
                                                            <td key={m} className="py-4 text-center px-1">
                                                                <div className="w-full py-1.5 rounded font-medium text-neutral-300 bg-neutral-50">—</div>
                                                            </td>
                                                        );
                                                    }
                                                    const percent = cohort.size > 0 ? Math.round((retainedCount / cohort.size) * 100) : 0;
                                                    const bgColor = percent > 80 ? 'bg-emerald-500'
                                                        : percent > 60 ? 'bg-emerald-400'
                                                        : percent > 40 ? 'bg-emerald-300'
                                                        : percent > 20 ? 'bg-emerald-200'
                                                        : 'bg-neutral-100';
                                                    const textColor = percent > 40 ? 'text-white' : 'text-neutral-900';
                                                    return (
                                                        <td key={m} className="py-4 text-center px-1">
                                                            <div className={cn('w-full py-1.5 rounded font-black transition-all group-hover:scale-105 shadow-sm', bgColor, textColor)}>
                                                                {percent}%
                                                            </div>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {data.retention.length === 0 && (
                                    <div className="py-12 text-center text-neutral-400 italic text-xs">Insufficient cohort data to display.</div>
                                )}
                            </CardContent>
                        </Card>
                    </div>
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center min-h-[400px] text-neutral-400">
                    <p>Failed to load platform value data.</p>
                </div>
            )}
        </div>
    );
};
